import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { CloudUpload, InsertDriveFile } from '@mui/icons-material';
import React, { useRef, useState } from 'react';
import { useImportDialogInbox } from '../hooks/reactQuerys/DialogInboxQueries';
import { useGetOpenFormulareDialogs } from '../hooks/reactQuerys/OpenFormulareDialogQueries';
import {
  DialogInboxImportFormat,
  IDialogInboxDetail,
} from '../Interfaces/IDialogInbox';

interface IProps {
  onClose: () => void;
  onImported: (entry: IDialogInboxDetail) => void;
  initialFormat?: DialogInboxImportFormat;
}

export const FORMAT_OPTIONS: {
  key: DialogInboxImportFormat;
  label: string;
  accept: string;
  hint: string;
}[] = [
  {
    key: 'openformulare',
    label: 'OpenFormulare (Datei)',
    accept: '.json,application/json',
    hint: 'Dialoge über DiNo-Mapping-Dateien importieren. Gedacht für Notarbüros, die OpenFormulare bereits vor DiNo genutzt haben und ihre bisherigen Einreichungen einspielen möchten.',
  },
  {
    key: 'xjustiz',
    label: 'XJustiz',
    accept: '.xml,.zip,application/xml,text/xml,application/zip',
    hint: 'XJustiz-XML oder ZIP-Bundle (DiNo-Export mit nachricht.xml + Dokumenten). Übernimmt Beteiligte; bei ZIP zusätzlich die mitgelieferten Dokumente (typischerweise Urkunden).',
  },
  {
    key: 'strukturdatensatz',
    label: 'Strukturdatensatz',
    accept: '.json,.zip,application/json,application/zip',
    hint: 'Strukturdatensatz-JSON oder ZIP-Bundle (mit strukturdatensatz.json + files/). Bei ZIP werden die referenzierten Dokumente als Urkunden / Entwürfe / Sonstige Dokumente am Vorgang abgelegt.',
  },
  {
    key: 'dino',
    label: 'DiNo',
    accept: '.json,application/json',
    hint: 'Übernahme eines Vorgangs, der zuvor aus DiNo exportiert wurde — etwa bei Übergabe an einen anderen Notar, beim Wiedereinspielen aus einer Sicherung oder beim Umzug zwischen Installationen. Alle Angaben bleiben erhalten.',
  },
  {
    key: 'notarnow',
    label: 'NotarNow',
    accept: '.json,application/json',
    hint: 'Unterstützte Formulararten: Erbschein, Erbauseinandersetzung, Erbausschlagung, Testament, Vorsorgevollmacht, Scheidungsvereinbarung, Ehevertrag, Schenkung, Adoption, Unternehmensgründung, Anteilskauf, Registeranmeldung, GbR und Verein.',
  },
];

export const DialogInboxImportDialog: React.FC<IProps> = ({
  onClose,
  onImported,
  initialFormat,
}) => {
  const [format, setFormat] = useState<DialogInboxImportFormat>(
    initialFormat ?? 'xjustiz'
  );
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { importDialogInbox, isImporting } = useImportDialogInbox();
  const { ofDialogs } = useGetOpenFormulareDialogs();
  // Aktive OpenFormulare-Verbindung => der Sync uebermittelt Dialoge bereits
  // automatisch. Beim manuellen OF-Datei-Import davor warnen (Doppel-Erfassung).
  const ofSyncActive = !!ofDialogs?.ofReachable;

  const activeOption = FORMAT_OPTIONS.find(o => o.key === format)!;

  const onPickFile = () => {
    inputRef.current?.click();
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(null);
    const f = event.target.files?.[0] ?? null;
    setFile(f);
  };

  const isZipFile = (f: File): boolean => {
    if (/\.zip$/i.test(f.name)) return true;
    const t = (f.type || '').toLowerCase();
    return t === 'application/zip' || t === 'application/x-zip-compressed';
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    // Stueckweise verarbeiten, damit String.fromCharCode nicht in stack-Limits laeuft.
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(
        null,
        Array.from(bytes.subarray(i, Math.min(i + chunk, bytes.length)))
      );
    }
    return window.btoa(binary);
  };

  const onSubmit = async () => {
    if (!file) {
      setErrorMessage('Bitte zuerst eine Datei auswählen.');
      return;
    }
    try {
      // Bei ZIP-Bundles binaer einlesen und base64-encoded ans Backend
      // schicken — der SDS- und XJustiz-Parser detektieren den ZIP-Magic
      // anhand des base64-Prefixes (UEsDB...).
      const content = isZipFile(file)
        ? arrayBufferToBase64(await file.arrayBuffer())
        : await file.text();
      const created = await importDialogInbox({
        format,
        content,
        filename: file.name,
      });
      onImported(created);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Unbekannter Fehler beim Import';
      setErrorMessage(message);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Dialog importieren</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              1. Importformat
            </Typography>
            <FormControl fullWidth size='small'>
              <InputLabel>Format</InputLabel>
              <Select
                label='Format'
                value={format}
                onChange={({ target: { value } }) => {
                  setFormat(value as DialogInboxImportFormat);
                  setFile(null);
                  setErrorMessage(null);
                }}
              >
                {FORMAT_OPTIONS.map(o => (
                  <MenuItem key={o.key} value={o.key}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mt: 1 }}
            >
              {activeOption.hint}
            </Typography>
            {format === 'notarnow' && (
              <Alert severity='info' sx={{ mt: 1 }}>
                NotarNow ist eine Marke des jeweiligen Rechteinhabers. Es
                besteht keine Verbindung oder Kooperation.
              </Alert>
            )}
            {format === 'openformulare' && ofSyncActive && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                Es besteht eine <strong>aktive OpenFormulare-Verbindung</strong>{' '}
                — eingehende Dialoge werden bereits automatisch synchronisiert.
                Nutzen Sie den Datei-Import nur für <strong>Alt-Daten</strong>,
                die nicht über den Sync kommen, um doppelte Einträge zu
                vermeiden. (Bereits synchronisierte Einreichungen werden anhand
                ihrer ID ohnehin nicht doppelt angelegt.)
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              2. Datei
            </Typography>
            <input
              ref={inputRef}
              type='file'
              accept={activeOption.accept}
              hidden
              onChange={onFileChange}
            />
            <Stack direction='row' alignItems='center' gap={2}>
              <Button
                variant='outlined'
                startIcon={<CloudUpload />}
                onClick={onPickFile}
              >
                Datei auswählen
              </Button>
              {file ? (
                <Chip
                  icon={<InsertDriveFile />}
                  label={`${file.name} (${Math.round(file.size / 1024)} kB)`}
                  onDelete={() => setFile(null)}
                />
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Keine Datei ausgewählt
                </Typography>
              )}
            </Stack>
          </Box>

          {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant='outlined' onClick={onClose} disabled={isImporting}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={onSubmit}
          disabled={!file || isImporting}
        >
          {isImporting ? 'Importiere…' : 'Importieren'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
